import { NotificationItem } from '@/components/notifications';
import notificationService from '@/services/notificationService';
import { Notification, NotificationQuery } from '@/types';
import {
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotifications, setTotalNotifications] = useState(0);

  const limit = 10;

  // Fetch notifications
  const fetchNotifications = async (page = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const query: NotificationQuery = {
        page,
        limit,
        isRead: filter === 'all' ? undefined : filter === 'read',
        type: typeFilter === 'all' ? undefined : typeFilter,
      };

      const response = await notificationService.getNotifications(query);

      if (response.success && response.data) {
        let filteredNotifications = response.data.notifications;

        // Client-side search filter
        if (searchTerm) {
          filteredNotifications = filteredNotifications.filter(
            notif =>
              notif.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
              notif.message.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        setNotifications(filteredNotifications);
        setCurrentPage(response.data.pagination.page);
        setTotalPages(response.data.pagination.pages);
        setTotalNotifications(response.data.pagination.total);
      } else {
        setError(response.message || 'Failed to fetch notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to fetch notifications');
    } finally {
      setIsLoading(false);
    }
  };

  // Load notifications when filters change
  useEffect(() => {
    fetchNotifications(1);
  }, [filter, typeFilter]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        fetchNotifications(1);
      } else {
        fetchNotifications(currentPage);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Mark notification as read
  const handleMarkAsRead = async (id: string) => {
    try {
      const response = await notificationService.markAsRead(id);
      if (response.success) {
        setNotifications(prev =>
          prev.map(notif => (notif._id === id ? { ...notif, isRead: true } : notif))
        );
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Delete notification
  const handleDelete = async (id: string) => {
    try {
      const response = await notificationService.deleteNotification(id);
      if (response.success) {
        setNotifications(prev => prev.filter(notif => notif._id !== id));
        setTotalNotifications(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationService.markAllAsRead();
      if (response.success) {
        setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Clear all notifications
  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to delete all notifications?')) {
      return;
    }

    try {
      const response = await notificationService.deleteAllNotifications();
      if (response.success) {
        setNotifications([]);
        setTotalNotifications(0);
        setCurrentPage(1);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already read
    if (!notification.isRead) {
      handleMarkAsRead(notification._id);
    }

    // Navigate to notification's action URL if it exists
    if (notification.actionUrl) {
      if (notification.actionUrl.startsWith('/')) {
        // Internal route
        navigate(notification.actionUrl);
      } else {
        // External URL
        window.open(notification.actionUrl, '_blank');
      }
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchNotifications(page);
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const notificationTypes = [
    'all',
    'info',
    'success',
    'warning',
    'error',
    'welcome',
    'verification',
    'security',
  ];

  return (
    <div className="max-w-7xl mx-auto py-6 px-2">
      <div className="bg-white shadow-sm rounded-xl border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Notifications</h1>
              <p className="mt-1 text-sm text-gray-600">
                {totalNotifications} total notifications
                {unreadCount > 0 && `, ${unreadCount} unread`}
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => fetchNotifications(currentPage)}
                disabled={isLoading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>

              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
                >
                  <CheckCheck className="w-4 h-4 mr-2" />
                  Mark All Read
                </button>
              )}

              {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
              />
            </div>

            {/* Read status filter */}
            <select
              value={filter}
              onChange={e => setFilter(e.target.value as 'all' | 'unread' | 'read')}
              className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-brand-primary focus:border-brand-primary"
            >
              <option value="all">All notifications</option>
              <option value="unread">Unread only</option>
              <option value="read">Read only</option>
            </select>

            {/* Type filter */}
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-brand-primary focus:border-brand-primary"
            >
              {notificationTypes.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All types' : type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-[400px]">
          {error && (
            <div className="p-6 text-center text-red-600 bg-red-50">
              <p className="text-sm">{error}</p>
              <button
                onClick={() => fetchNotifications(currentPage)}
                className="mt-2 text-xs text-red-700 underline"
              >
                Try again
              </button>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading notifications...</span>
            </div>
          )}

          {!isLoading && !error && notifications.length === 0 && (
            <div className="p-12 text-center text-gray-500">
              <div className="text-6xl mb-4">🔔</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
              <p className="text-sm">
                {searchTerm || filter !== 'all' || typeFilter !== 'all'
                  ? 'No notifications match your current filters'
                  : "You're all caught up! No notifications to display."}
              </p>
            </div>
          )}

          {!isLoading && !error && notifications.length > 0 && (
            <div className="divide-y divide-gray-200">
              {notifications.map(notification => (
                <div key={notification._id} className="p-4">
                  <NotificationItem
                    notification={notification}
                    onMarkRead={handleMarkAsRead}
                    onDelete={handleDelete}
                    onClick={handleNotificationClick}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing page {currentPage} of {totalPages} ({totalNotifications} total)
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <span className="text-sm text-gray-500">
                {currentPage} / {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
